import { CalendarEventType, HolidayType, logError, OrderStatus } from '@/composables';
import { showToast } from '@/composables/swal';
import { http } from '@/interceptors/axiosRequestInterceptor';
import {
  addDays,
  endOfMonth,
  endOfWeek,
  endOfYear,
  format,
  isAfter,
  isBefore,
  isEqual,
  parseISO,
  startOfDay,
  startOfMonth,
  startOfWeek,
  startOfYear,
} from 'date-fns';
import { defineStore } from 'pinia';
import Swal from 'sweetalert2';

export interface DashboardTab {
  display_name: string;
  count: number;
  key: string;
  cleaned_key: string;
  apiKey: string;
}

// Update interfaces to use enums
export interface Event {
  id: string;
  title: string;
  start: string; // ISO date string
  end?: string; // ISO date string
  time?: string; // Time string (HH:MM)
  type?: CalendarEventType | string;
  status?: OrderStatus;
  notes?: string;
  allDay?: boolean;
  new_date?: string; // For drag and drop - the new date when event is moved
  order_item_id?: string | number | null; // For drag and drop - the order item ID
  [key: string]: any;
}

interface Holiday {
  id: string;
  name: string;
  date: string;
  type: HolidayType | string;
  country_code?: string;
}

export interface CalendarEvent {
  id: string;
  title: string;
  start: string; // ISO date string
  end?: string; // ISO date string
  time?: string; // Time string (HH:MM)
  type?: CalendarEventType | string;
  status?: OrderStatus;
  notes?: string;
  allDay?: boolean;
  priority?: 'low' | 'medium' | 'high';
  new_date?: string; // For drag and drop - the new date when event is moved
  order_item_id?: string | number | null; // For drag and drop - the order item ID
  [key: string]: any;
}

interface CalendarFilters {
  items_for?: string;
  [key: string]: any;
}

interface OrderInfo {
  id: string;
  order_id: string;
  order_no: string;
  order_title: string;
  customer: string;
  shipping_address: string;
  status: string;
  notes: string;
  start: string;
}

export const useDashboardStore = defineStore('dashboard', {
  state: () => {
    const today = new Date();
    const year = today.getFullYear();

    return {
      // Dashboard tabs and mode
      dashboardTabs: [] as DashboardTab[],
      modeTabs: [
        { id: 'dashboard', label: 'Dashboard' },
        { id: 'calendar', label: 'Calendar' },
        { id: 'my-tasks', label: 'My Tasks' },
      ],
      currentMode: 'dashboard',

      // Calendar filters and search
      orderNoSearch: '',
      eventTypeFilter: '',
      eventStatusFilter: '',
      selectedCalendarDate: today,
      selectedHolidayCountryCode: 'dk',

      // Calendar view state
      currentView: 'month' as 'day' | 'week' | 'month' | 'year',
      calendarKey: 0,
      calendarInitialized: false,

      // Calendar data
      events: [] as Event[],
      holidays: [] as Holiday[],
      calendarFilters: null as CalendarFilters | null,

      // Order management
      orderList: [] as any[],
      selectedOrder: null as any,
      orderInfo: {
        id: '',
        order_id: '',
        order_no: '',
        order_title: '',
        customer: '',
        shipping_address: '',
        status: 'pending',
        notes: '',
        start: '',
      } as OrderInfo,

      // Loading states
      isOrderLoading: false,
      isCalendarLoading: false,
    };
  },

  getters: {
    // Computed properties for calendar
    selectedYear: (state) => {
      return format(state.selectedCalendarDate, 'yyyy');
    },

    startDate: (state) => {
      const date = state.selectedCalendarDate;

      switch (state.currentView) {
        case 'day':
          return format(date, 'yyyy-MM-dd');
        case 'week':
          return format(startOfWeek(date), 'yyyy-MM-dd');
        case 'month':
          return format(startOfMonth(date), 'yyyy-MM-dd');
        case 'year':
          return format(startOfYear(date), 'yyyy-MM-dd');
        default:
          return format(date, 'yyyy-MM-dd');
      }
    },

    endDate: (state) => {
      const date = state.selectedCalendarDate;

      switch (state.currentView) {
        case 'day':
          return format(date, 'yyyy-MM-dd');
        case 'week':
          return format(endOfWeek(date), 'yyyy-MM-dd');
        case 'month':
          return format(endOfMonth(date), 'yyyy-MM-dd');
        case 'year':
          return format(endOfYear(date), 'yyyy-MM-dd');
        default:
          return format(date, 'yyyy-MM-dd');
      }
    },

    orderOptions: (state) => {
      return state.orderList.map((order) => ({
        ...order,
        label: order.customer_reference_no
          ? `${order.order_no} (${order.customer_reference_no})`
          : order.order_no,
      }));
    },

    queryParams: (state) => {
      const params = new URLSearchParams();
      if (state.selectedHolidayCountryCode) {
        params.append('country_code', state.selectedHolidayCountryCode);
        params.append('year', format(state.selectedCalendarDate, 'yyyy'));
      }

      // Calculate start and end dates based on current view
      const date = state.selectedCalendarDate;
      let startDate: string;
      let endDate: string;

      switch (state.currentView) {
        case 'day':
          startDate = format(date, 'yyyy-MM-dd');
          endDate = format(date, 'yyyy-MM-dd');
          break;
        case 'week':
          startDate = format(startOfWeek(date), 'yyyy-MM-dd');
          endDate = format(endOfWeek(date), 'yyyy-MM-dd');
          break;
        case 'month':
          startDate = format(startOfMonth(date), 'yyyy-MM-dd');
          endDate = format(endOfMonth(date), 'yyyy-MM-dd');
          break;
        case 'year':
          startDate = format(startOfYear(date), 'yyyy-MM-dd');
          endDate = format(endOfYear(date), 'yyyy-MM-dd');
          break;
        default:
          startDate = format(date, 'yyyy-MM-dd');
          endDate = format(date, 'yyyy-MM-dd');
      }

      params.append('start_date', startDate);
      params.append('end_date', endDate);

      if (state.currentMode === 'calendar' && state.calendarFilters) {
        const myAssigned = state.calendarFilters.items_for === 'my_assigned_orders';
        params.append('my_assigned_orders', myAssigned ? 'true' : 'false');
      }

      return params.toString();
    },

    calendarFilterPayload: (state) => {
      if (state.currentMode !== 'calendar' || !state.calendarFilters) return {};

      // Only extract quick_filters from calendarFilters
      const { quick_filters } = state.calendarFilters;

      if (quick_filters && typeof quick_filters === 'object') {
        return { quick_filters };
      }

      // If quick_filters is not present, build it from other keys
      const { items_for, ...otherFilters } = state.calendarFilters;
      const quickFilters = {};
      Object.entries(otherFilters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          if (key === 'factory_ids') {
            quickFilters['factory_ids'] = Array.isArray(value) ? value : [value];
            return;
          }
          if (Array.isArray(value)) {
            quickFilters[key] = value.map((v) =>
              typeof v === 'object' && v !== null && 'value' in v ? v.value : v
            );
          } else if (
            typeof value === 'object' &&
            !Array.isArray(value) &&
            value !== null &&
            'value' in value
          ) {
            quickFilters[key] = value.value;
          } else {
            quickFilters[key] = value;
          }
        }
      });
      return { quick_filters: quickFilters };
    },
  },

  actions: {
    // Dashboard tab management
    setDashboardTabs(tabs: DashboardTab[]) {
      this.dashboardTabs = tabs;
    },

    setCurrentMode(mode: string) {
      this.currentMode = mode;
    },

    // Calendar filter management
    setOrderNoSearch(val: string) {
      this.orderNoSearch = val;
    },

    setEventTypeFilter(val: string) {
      this.eventTypeFilter = val;
    },

    setEventStatusFilter(val: string) {
      this.eventStatusFilter = val;
    },

    setCalendarFilters(filters: CalendarFilters | null) {
      this.calendarFilters = filters;
    },

    // Calendar view management
    setCurrentView(view: string) {
      this.currentView = view;
    },

    setSelectedDate(date: Date) {
      this.selectedCalendarDate = date;
    },

    setSelectedHolidayCountryCode(code: string) {
      this.selectedHolidayCountryCode = code;
    },

    // Initialize calendar with proper date
    initializeCalendar() {
      if (!this.calendarInitialized) {
        const today = new Date();
        this.selectedCalendarDate = today;
        this.currentView = 'month';
        this.selectedHolidayCountryCode = 'dk';
        this.calendarInitialized = true;
      }
    },

    // Event management
    setEvents(events: Event[]) {
      this.events = events;
    },

    addEvent(event: Event) {
      this.events.push(event);
    },

    updateEvent(updated: Event) {
      const idx = this.events.findIndex((e) => e.id === updated.id);
      if (idx !== -1) this.events[idx] = updated;
    },

    deleteEvent(id: string) {
      this.events = this.events.filter((e) => e.id !== id);
    },

    // Holiday management
    setHolidays(holidays: Holiday[]) {
      this.holidays = holidays;
    },

    addHoliday(holiday: Holiday) {
      this.holidays.push(holiday);
    },

    deleteHoliday(id: string) {
      this.holidays = this.holidays.filter((h) => h.id !== id);
    },

    // Order management
    setOrderList(orders: any[]) {
      this.orderList = orders;
    },

    setSelectedOrder(order: any) {
      this.selectedOrder = order;
      if (order) {
        this.orderInfo = {
          order_id: order.order_id ?? order.id,
          order_no: order.order_no,
          order_title: order.customer_reference_no || order.order_no || '',
          customer: order.customer_name || '',
          shipping_address: this.formatShippingAddress(order.shipping_address),
          status: order.status || 'pending',
          notes: order.notes || order.additional_fields?.order_detail_object?.customer_note || '',
          start: order.start
            ? format(new Date(order.start), 'yyyy-MM-dd')
            : format(new Date(), 'yyyy-MM-dd'),
          id: order.id || '',
        };
      }
    },

    resetOrderInfo() {
      this.orderInfo = {
        id: '',
        order_id: '',
        order_no: '',
        order_title: '',
        customer: '',
        shipping_address: '',
        status: 'pending',
        notes: '',
        start: '',
      };
      this.selectedOrder = null;
    },

    // Utility functions
    formatShippingAddress(addr: any): string {
      if (!addr) return '';
      return [
        addr.address1 || addr.address_1,
        addr.address2 || addr.address_2,
        addr.city,
        addr.zip_code || addr.postcode,
        addr.country,
        addr.company,
      ]
        .filter(Boolean)
        .join(', ');
    },

    getEventColorClass(event: Event) {
      if (event.type === 'reminder') {
        return 'bg-purple-100 border-l-4 border-purple-500';
      } else if (event.type === 'meeting') {
        return 'bg-blue-100 border-l-4 border-blue-500';
      } else if (event.type === 'appointment') {
        return 'bg-green-100 border-l-4 border-green-500';
      }

      switch (event.status) {
        case 'pending':
          return 'bg-blue-100 border-l-4 border-blue-500';
        case 'shipped':
          return 'bg-green-100 border-l-4 border-green-500';
        case 'delayed':
          return 'bg-red-100 border-l-4 border-red-500';
        case 'processing':
        case 'completed':
        case 'cancelled':
        default:
          return 'bg-surface-2 border-l-4 border-gray-500';
      }
    },

    // Data transformation functions
    splitEvents(events: any[]) {
      return events.reduce(
        (result, event) => {
          if (event.event_type === CalendarEventType.Holiday) {
            result.holidays.push(event);
          } else {
            result.others.push(event);
          }
          return result;
        },
        { holidays: [], others: [] }
      );
    },

    transformEvents(rawEvents: any[]): Event[] {
      return rawEvents
        .map((event) => {
          if (event.event_type === CalendarEventType.Holiday) return null;

          // Handle shipping_address (object or string)
          let shipping = {};
          if (event.shipping_address) {
            if (typeof event.shipping_address === 'string') {
              try {
                shipping = JSON.parse(event.shipping_address);
              } catch {
                shipping = {};
              }
            } else if (typeof event.shipping_address === 'object') {
              shipping = event.shipping_address;
            }
          }

          // Extract order/customer info
          const order = event.order || {};
          const customer = event.customer || {};
          return {
            id: event.id,
            title: event.title ?? this.getEventTitle(event),
            order_id: order.id ?? null,
            order_no: order.order_no ?? null,
            customer_id: customer.id ?? null,
            customer_name: customer.customer_name ?? null,
            shipping_address: shipping,
            country_code: event.country_code ?? null,
            country_name: event.country_name ?? null,
            start: event.start_date || format(new Date(), 'yyyy-MM-dd'),
            end: event.end_date ?? null,
            type: event.event_type || CalendarEventType.Order,
            status: event.state || event.status || 'pending',
            notes: event.description ?? '',
            event_log: event.event_log ?? [],
            event_meta_info: event.event_meta_info ?? [],
            created_by: event.created_by ?? null,
            time: '09:00',
          };
        })
        .filter(Boolean);
    },

    transformToHolidays(rawEvents: any[]): Holiday[] {
      return rawEvents
        .map((event) => {
          const year =
            event.start_year ||
            (event.start_date ? event.start_date.slice(0, 4) : new Date().getFullYear());
          if (event.event_type === CalendarEventType.Holiday) {
            return {
              id: event.id,
              name: event.title || 'Untitled Holiday',
              date: event.start_date || `${year}-01-01`,
              type: event.event_meta_info?.type[0] || HolidayType.Public,
              country_code: event.country_code || 'US',
            };
          }
        })
        .filter(Boolean);
    },

    transformToCalenderEventPayload(event: CalendarEvent): Record<string, any> {
      return {
        title: event.title ?? null,
        order_id: event.order_id ?? null,
        order_no: event.order_no ?? null,
        customer_id: event.customer_id ?? null,
        customer_name: event.customer_name ?? null,
        shipping_address: event.shipping_address ?? null,
        country_code: event.country_code ?? null,
        country_name: event.country_name ?? null,
        start_date: event.start ?? null,
        end_date: event.end ?? null,
        event_type: event.type ?? 'event',
        description: event.notes ?? null,
        status: event.status ?? 'pending',
        event_log: event.event_log ?? [],
        event_meta_info: event.event_meta_info ?? [],
        created_by: event.created_by ?? null,
        new_date: event.new_date ?? null,
        order_item_id: event.order_item_id ?? null,
      };
    },

    getEventTitle(event: any): string {
      if (event.event_type === CalendarEventType.Event) {
        return event.title;
      }
      if (event.event_type === CalendarEventType.Order) {
        // Whether an order carries a CUSTOMER REFERENCE says nothing about
        // whether it has an order number, so this used to branch on one and
        // switch identifiers on the other: an order with no customer reference
        // was titled with its internal database id, labelled "Order ID #". The
        // two number spaces overlap, so a calendar entry read "Order ID #19366"
        // while a real order #19366 existed elsewhere in the system (AB#21223,
        // same fault as the delete confirmation).
        //
        // The order number is the only identifier a person outside this
        // codebase can act on. An order that does not have one yet is named as
        // such rather than given a number that means something else.
        return event.order?.order_no ? `Order #${event.order.order_no}` : 'Order (not yet numbered)';
      }
      return 'Untitled Event';
    },

    // API functions
    async searchOrders(query: string, signal?: AbortSignal) {
      if (!query) {
        if (this.selectedOrder) {
          this.orderList = [this.selectedOrder];
        } else {
          this.orderList = [];
        }
        return;
      }

      try {
        this.isOrderLoading = true;
        const res = await http.post('/search-orders', { query }, { signal });
        this.orderList = res.data.result || [];
      } catch (err) {
        // Don't update orderList if request was aborted
        if (err.name !== 'AbortError') {
          this.orderList = [];
          logError(err);
        }
      } finally {
        this.isOrderLoading = false;
      }
    },

    getOrderEventState(events) {
      const today = startOfDay(new Date());
      return events.map((event) => {
        let state = event.status;
        if (event.status === 'in_production') {
          const eventDate = event.start_date ? startOfDay(parseISO(event.start_date)) : null;
          if (eventDate) {
            if (isAfter(today, eventDate)) {
              state = 'delayed';
            } else if (isBefore(today, eventDate) || isEqual(today, eventDate)) {
              state = 'pending';
            }
          }
        } else if (event.status === 'shipped') {
          state = 'shipped';
        }
        return { ...event, state };
      });
    },

    async fetchCalendarData() {
      try {
        this.isCalendarLoading = true;
        const res = await http.post(
          `/calendar-events/filters?${this.queryParams}`,
          this.calendarFilterPayload
        );
        const events = res.data.result.events;
        const { holidays, others } = this.splitEvents(events);
        const othersWithState = this.getOrderEventState(others);
        this.events = this.transformEvents(othersWithState);
        this.holidays = this.transformToHolidays(holidays);
        this.calendarKey++; // Force re-render
      } catch (err) {
        logError(err);
      } finally {
        this.isCalendarLoading = false;
      }
    },

    getEventType(event) {
      return event?.type ?? 'holiday';
    },

    async handleEventUpdated(event: CalendarEvent) {
      this.isCalendarLoading = true;

      // Extract additional fields for drag and drop
      const newDate = event.new_date;

      let payload;
      let endpoint;
      let method;

      if (newDate) {
        payload = {
          new_date: newDate,
          event_id: event.event_id,
          event_type: this.getEventType(event),
        };
        endpoint = '/update-shipping-date';
        method = 'post';
      } else {
        payload = this.transformToCalenderEventPayload(event);
        Object.keys(payload).forEach(
          (key) => (payload[key] === null || payload[key] === '') && delete payload[key]
        );
        endpoint = `/calendar-events/${event.id}`;
        method = 'patch';
      }

      try {
        await http[method](endpoint, payload);
        await Swal.fire({
          title: 'Success!',
          text: newDate ? 'Shipping date has been updated.' : 'Event has been updated.',
          icon: 'success',
          confirmButtonColor: '#4f46e5',
        });
      } catch (err) {
        logError(err);
      } finally {
        this.isCalendarLoading = false;
      }
    },

    async handleEventCreated(event: CalendarEvent) {
      this.isCalendarLoading = true;
      try {
        const payload = this.transformToCalenderEventPayload(event);
        await http.post(`/calendar-events`, payload);
        await Swal.fire({
          title: 'Success!',
          text: 'Event has been created.',
          icon: 'success',
          confirmButtonColor: '#4f46e5',
        });

        // Navigate to the month where the event was created
        if (event.start) {
          const eventDate = new Date(event.start);
          this.selectedCalendarDate = eventDate;
        }

        await this.fetchCalendarData();
      } catch (err) {
        logError(err);
      } finally {
        this.isCalendarLoading = false;
      }
    },

    async handleEventDeleted(eventId: string) {
      this.isCalendarLoading = true;
      try {
        await http.delete(`/calendar-events/${eventId}`);
        await this.fetchCalendarData();
      } catch (err) {
        logError(err);
      } finally {
        this.isCalendarLoading = false;
      }
    },

    async handleHolidayAdded(holidayEvent: any) {
      const payload = {
        country_code: this.selectedHolidayCountryCode,
        title: holidayEvent.name || 'Untitled Holiday',
        start_date: holidayEvent.date,
        event_type: 'holiday',
        event_meta_info: {
          type: [holidayEvent.type || 'public'],
        },
      };

      this.isCalendarLoading = true;
      try {
        await http.post(`/calendar-events`, payload);
        await Swal.fire({
          title: 'Success!',
          text: 'Holiday has been created.',
          icon: 'success',
          confirmButtonColor: '#4f46e5',
        });
        await this.fetchCalendarData();
      } catch (err) {
        logError(err);
      } finally {
        this.isCalendarLoading = false;
      }
    },

    async handleHolidayRemoved(holiday: Holiday) {
      this.isCalendarLoading = true;
      try {
        await http.delete(`/calendar-events/${holiday.id}`);
        await Swal.fire({
          title: 'Success!',
          text: 'Holiday has been deleted.',
          icon: 'success',
          confirmButtonColor: '#4f46e5',
        });
        await this.fetchCalendarData();
      } catch (err) {
        logError(err);
      } finally {
        this.isCalendarLoading = false;
      }
    },

    async handleHolidayUpdated(holiday: Holiday) {
      this.isCalendarLoading = true;
      try {
        const payload = {
          country_code: this.selectedHolidayCountryCode,
          title: holiday.name || 'Untitled Holiday',
          start_date: holiday.date,
          event_type: 'holiday',
          event_meta_info: {
            type: [holiday.type || 'public'],
          },
        };

        await http.patch(`/calendar-events/${holiday.id}`, payload);
        await Swal.fire({
          title: 'Success!',
          text: 'Holiday has been updated.',
          icon: 'success',
          confirmButtonColor: '#4f46e5',
        });
        await this.fetchCalendarData();
      } catch (err) {
        console.error('Error updating holiday:', err);
        logError(err);
      } finally {
        this.isCalendarLoading = false;
      }
    },

    async exportCalendarData() {
      try {
        this.isCalendarLoading = true;
        const response = await http.get(`calendar-events/export?${this.queryParams}`, {
          responseType: 'blob',
        });
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'calendar_events.xlsx');
        document.body.appendChild(link);
        link.click();
        link.remove();
      } catch (error) {
        console.error(error);
      } finally {
        this.isCalendarLoading = false;
      }
    },

    jumpToOrderQueryParams() {
      const params = new URLSearchParams();
      params.append('view', this.currentView);
      // Always include country_code if available
      if (this.selectedHolidayCountryCode) {
        params.append('country_code', this.selectedHolidayCountryCode);
      }
      // Add my_assigned_orders if calendarFilters exists and items_for is 'my_assigned_orders'
      if (this.calendarFilters && this.calendarFilters.items_for === 'my_assigned_orders') {
        params.append('my_assigned_orders', 'true');
      }
      return params.toString();
    },

    async jumpToOrder() {
      if (!this.selectedOrder) {
        // Remove order_no from calendarFilters if present
        if (this.calendarFilterPayload && this.calendarFilterPayload.order_no) {
          delete this.calendarFilterPayload.order_no;
        }
        // Reset calendar to today
        this.selectedCalendarDate = new Date();
        await this.fetchCalendarData();
        return;
      }

      const params = this.jumpToOrderQueryParams();

      const payload = this.calendarFilterPayload;
      payload.order_no = this.selectedOrder.order_no;

      // Fetch calendar data for this order and filters
      try {
        this.isCalendarLoading = true;
        const res = await http.post(`/calendar-events/filters?${params}`, payload);
        const events = res.data.result.events;
        const { holidays, others } = this.splitEvents(events);
        const othersWithState = this.getOrderEventState(others);
        this.events = this.transformEvents(othersWithState);
        this.holidays = this.transformToHolidays(holidays);

        // If a matching event is found, jump to its date and year
        if (othersWithState.length > 0) {
          const firstEvent = othersWithState[0];
          if (firstEvent.start_date) {
            this.selectedCalendarDate = new Date(firstEvent.start_date);
          }
        } else {
          // Show toast if no events found
          showToast('Order not found', 'error');
        }
        this.calendarKey++; // Force re-render
      } catch (err) {
        logError(err);
      } finally {
        this.isCalendarLoading = false;
      }
    },
  },
});
